/**
 * Video Element Editor
 * Uses MediaEdit for src field, custom controls for playback options
 */

import * as React from 'react';
import {
    cn,
    Label,
    Input,
    Switch,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@ai-content-hub/content-kit';
import type { ElementEditorProps } from '@ai-content-hub/content-kit';
import { MediaEdit } from '../media';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { videoElementSchema, type VideoElementData } from './schema';

export interface VideoEditProps extends ElementEditorProps<VideoElementData> {}

export const VideoEdit: React.FC<VideoEditProps> = ({
    value,
    onChange,
    errors,
    disabled,
    className,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? videoElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    // Handle src change from MediaEdit
    const handleSrcChange = React.useCallback(
        (mediaData: { src: string; mediaType: string; title?: string }) => {
            onChange({
                ...value,
                src: mediaData.src,
                // Use media title if title is empty
                title: value.title || mediaData.title || '',
            });
        },
        [value, onChange]
    );

    // Handle poster change from MediaEdit
    const handlePosterChange = React.useCallback(
        (mediaData: { src: string; mediaType: string; title?: string }) => {
            onChange({
                ...value,
                poster: mediaData.src,
            });
        },
        [value, onChange]
    );

    // Handle field change
    const handleFieldChange = React.useCallback(
        <K extends keyof VideoElementData>(field: K, fieldValue: VideoElementData[K]) => {
            onChange({
                ...value,
                [field]: fieldValue,
            });
        },
        [value, onChange]
    );

    return (
        <div className={cn('video-edit space-y-6', className)}>
            {/* Video Source Section */}
            <div className="space-y-3">
                <Label className="text-sm font-medium">{t('videoSource')}</Label>
                <MediaEdit
                    value={{
                        src: value.src || '',
                        mediaType: 'video',
                        title: value.title || '',
                    }}
                    onChange={handleSrcChange}
                    mediaType="video"
                    layout="default"
                    disabled={disabled}
                    config={config}
                    language={language}
                    displayConfig={{
                        fit: 'contain',
                        maxHeight: 200,
                        aspectRatio: value.aspectRatio || '16:9',
                    }}
                />
                {errors?.src && (
                    <p className="text-sm text-destructive">{errors.src}</p>
                )}
            </div>

            {/* Title */}
            <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-medium">
                    {t('title', 'Title')}
                </Label>
                <Input
                    id="title"
                    value={value.title || ''}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    placeholder={t('videoTitlePlaceholder')}
                    disabled={disabled}
                />
            </div>

            {/* Poster Image Section */}
            <div className="space-y-3">
                <Label className="text-sm font-medium">{t('posterImage')}</Label>
                <p className="text-xs text-muted-foreground">
                    {t('posterImageHint')}
                </p>
                <MediaEdit
                    value={{
                        src: value.poster || '',
                        mediaType: 'image',
                        title: t('videoPosterMediaTitle'),
                    }}
                    onChange={handlePosterChange}
                    mediaType="image"
                    layout="default"
                    disabled={disabled}
                    config={config}
                    language={language}
                    displayConfig={{
                        fit: 'cover',
                        maxHeight: 120,
                        aspectRatio: value.aspectRatio || '16:9',
                    }}
                />
            </div>

            {/* Aspect Ratio */}
            <div className="space-y-2">
                <Label htmlFor="aspectRatio" className="text-sm font-medium">
                    {t('aspectRatio')}
                </Label>
                <Select
                    value={value.aspectRatio || '16:9'}
                    onValueChange={(v) => handleFieldChange('aspectRatio', v as VideoElementData['aspectRatio'])}
                    disabled={disabled}
                >
                    <SelectTrigger id="aspectRatio">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="16:9">{t('aspect169')}</SelectItem>
                        <SelectItem value="4:3">{t('aspect43')}</SelectItem>
                        <SelectItem value="1:1">{t('aspect11')}</SelectItem>
                        <SelectItem value="9:16">{t('aspect916')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Playback Options */}
            <div className="space-y-4">
                <Label className="text-sm font-medium">{t('playbackOptions')}</Label>
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                        <Label htmlFor="autoplay" className="text-sm cursor-pointer">
                            {t('autoplay')}
                        </Label>
                        <Switch
                            id="autoplay"
                            checked={value.autoplay || false}
                            onCheckedChange={(checked) => handleFieldChange('autoplay', checked)}
                            disabled={disabled}
                        />
                    </div>

                    <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                        <Label htmlFor="loop" className="text-sm cursor-pointer">
                            {t('loop')}
                        </Label>
                        <Switch
                            id="loop"
                            checked={value.loop || false}
                            onCheckedChange={(checked) => handleFieldChange('loop', checked)}
                            disabled={disabled}
                        />
                    </div>

                    <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                        <Label htmlFor="muted" className="text-sm cursor-pointer">
                            {t('muted')}
                        </Label>
                        <Switch
                            id="muted"
                            checked={value.muted || false}
                            onCheckedChange={(checked) => handleFieldChange('muted', checked)}
                            disabled={disabled}
                        />
                    </div>

                    <div className="flex items-center justify-between gap-2 p-3 rounded-lg border">
                        <Label htmlFor="showControls" className="text-sm cursor-pointer">
                            {t('showControls')}
                        </Label>
                        <Switch
                            id="showControls"
                            checked={value.showControls !== false}
                            onCheckedChange={(checked) => handleFieldChange('showControls', checked)}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoEdit;
